import cn from 'classnames'
import React, { memo, useCallback } from 'react'

import { useGetFormData } from 'cng-common/features/form'
import { IFormDropdownProps } from 'cng-common/features/form/components/formDropdown/models'
import { TFormMultiselectProps } from 'cng-common/features/form/components/formMultiselect/models'
import { IFormRichTextEditor } from 'cng-common/features/form/components/formRichTextEditor/models'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { Crud } from 'core/types/Crud'
import { IPartnerTerritory } from 'onboarding/classes/models/partnerTerritoryModels'
import {
  FormLocationTreeAdapter,
  OnboardingFormDropdownAdapter,
  OnboardingFormMultiselectAdapter,
  OnboardingFormRichTextEditorAdapter,
} from 'onboarding/components/ui/visualAudienceAdapters'

import { TFormLocationTreeExtended } from '../formLocationTreeExtended/FormLocationTreeExtended'
import { TStaticCrudItem } from '../models'
import { PartnerTerritoryInfo } from '../partnerTerritoryInfo/PartnerTerritoryInfo'

import { TRequestorToFillLAT1Form } from './models'

import styles from './RequestorFillLAT1.module.scss'

interface IProps {
  formKey: string
  partnerTerritory: IPartnerTerritory
  hpContractAdminLocation: string
  buyerType: string
  partnerTerritoryCodes: string[]
  specialPrograms: string[]
  exhibits: string
  additionalComments: string
  writeAudience: IAudienceCondition
  cruds: {
    HPContractAdminLocation: Crud<any, any>
    BuyerType: Crud<any, any>
    SpecialPrograms: Crud<any, any>
  }
  isDisabled?: boolean
}

export const FormFields = memo(
  ({
    formKey,
    partnerTerritory,
    hpContractAdminLocation,
    buyerType,
    partnerTerritoryCodes,
    specialPrograms,
    exhibits,
    additionalComments,
    writeAudience,
    cruds,
    isDisabled,
  }: IProps) => {
    const [{ formData }, handleChange] = useGetFormData<TRequestorToFillLAT1Form>(formKey)
    const resetSpecialPrograms = useCallback(
      () =>
        handleChange({
          field: 'SpecialPrograms',
          value: [],
        }),
      [handleChange]
    )

    return (
      <div className={styles.root}>
        <div className={styles.root__rightColumn}>
          <div>
            <div className={styles.text__title}>Information about the request contract</div>
            <div className={styles.wrapper}>
              <VisualAudienceExtended<IFormDropdownProps<TStaticCrudItem>>
                write={writeAudience}
                component={OnboardingFormDropdownAdapter}
                formKey={formKey}
                field={'HPContractAdminLocation'}
                label={'HP Contract Admin Location'}
                defaultValue={hpContractAdminLocation}
                source={cruds.HPContractAdminLocation}
                caption={'label'}
                onChange={resetSpecialPrograms}
                isDisabled={isDisabled}
                isRequired
              />
            </div>

            <PartnerTerritoryInfo />

            <div className={cn(styles.wrapper, styles.partnerTerritoryWrapper)}>
              <VisualAudienceExtended<TFormLocationTreeExtended>
                component={FormLocationTreeAdapter}
                partnerTerritory={partnerTerritory}
                write={writeAudience}
                formKey={formKey}
                field={'PartnerTerritoryISO'}
                label={'Partner Territory'}
                defaultValue={partnerTerritoryCodes}
                root={'Region-AMS'}
                isDisabled={isDisabled}
                hideHpOrg
                isRequired
              />
            </div>

            <div className={styles.wrapper}>
              <VisualAudienceExtended<IFormDropdownProps<TStaticCrudItem>>
                component={OnboardingFormDropdownAdapter}
                write={writeAudience}
                formKey={formKey}
                field={'BuyerType'}
                label={'Buyer Type'}
                defaultValue={buyerType}
                source={cruds.BuyerType}
                caption={'label'}
                onChange={resetSpecialPrograms}
                isDisabled={isDisabled}
                isStatic
                isRequired
              />
            </div>
            <div className={styles.wrapper}>
              <VisualAudienceExtended<TFormMultiselectProps<TStaticCrudItem>>
                component={OnboardingFormMultiselectAdapter}
                write={writeAudience}
                formKey={formKey}
                source={cruds.SpecialPrograms}
                caption={'label'}
                label={'Special Programs'}
                field={'SpecialPrograms'}
                defaultValue={specialPrograms}
                isStatic
                isDisabled={!(formData.BuyerType && formData.HPContractAdminLocation) || isDisabled}
              />
            </div>
          </div>
          <div>
            <div className={styles.text__title}>Requested Exhibits</div>
            <div className={styles.text}>Exhibits will require to be approved:</div>
            <div className={styles.wrapper}>
              <VisualAudienceExtended<IFormRichTextEditor>
                component={OnboardingFormRichTextEditorAdapter}
                write={writeAudience}
                formKey={formKey}
                defaultValue={exhibits}
                field={'Exhibits'}
                placeholder={'Add exhibits...'}
                isDisabled={isDisabled}
                isRequired
              />
            </div>

            <div className={styles.text}>
              Additional comments (anything that you need the Contracts Team to consider from your
              application):
            </div>
            <div className={styles.wrapper}>
              <VisualAudienceExtended<IFormRichTextEditor>
                component={OnboardingFormRichTextEditorAdapter}
                write={writeAudience}
                formKey={formKey}
                defaultValue={additionalComments}
                field={'AdditionalComments'}
                placeholder={'Add additional comments...'}
                isDisabled={isDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
)
